/**
 * Scrollable report results table with a frozen header and frozen
 * leading columns. Header cells stay on one line. Body cells wrap
 * when the template asks for a line break after a character limit.
 */
import React from 'react';

import SortableTh from '../../components/SortableTh.jsx';
import { useTableSort } from '../../lib/tableSort.js';
import { isQuestionColumn } from './reportText.js';

export function UniFormsReportTable({
  payload,
  columns,
  rowKey = 'response_id',
}) {
  const freeze = payload?.freeze || { rows: 1, columns: 0 };
  const frozenColCount = freeze.columns || 0;
  const tableRef = React.useRef(null);
  const [colOffsets, setColOffsets] = React.useState([]);

  const rows = payload?.rows || [];
  const { sortedRows, sort, toggleSort } = useTableSort(rows, {
    columns,
    getValue: (row, key) => row.cells?.[key] ?? row[key],
  });

  React.useLayoutEffect(() => {
    const table = tableRef.current;
    if (!table || frozenColCount < 1) {
      setColOffsets((prev) => (prev.length ? [] : prev));
      return undefined;
    }
    const headerCells = table.querySelectorAll('thead th');
    let acc = 0;
    const next = [];
    headerCells.forEach((cell, i) => {
      next[i] = acc;
      if (i < frozenColCount) acc += cell.offsetWidth;
    });
    setColOffsets((prev) => {
      if (
        prev.length === next.length
        && prev.every((v, i) => v === next[i])
      ) {
        return prev;
      }
      return next;
    });
    return undefined;
  }, [columns, frozenColCount, rows, payload]);

  if (!rows.length) {
    return (
      <div className="uni-state">
        <div className="uni-state__title">Nothing matched</div>
        <div className="uni-state__hint">No submissions for that scope, period and filters.</div>
      </div>
    );
  }

  const presentation = payload?.meta?.text_presentation;
  const breakText = presentation?.mode === 'line_break';
  const breakQuestions = breakText && !!presentation?.apply_to_question_column;
  const tableClass = [
    'ty-table',
    'uni-report-table',
    breakText ? 'uni-report-table--break' : '',
    breakQuestions ? 'uni-report-table--break-questions' : '',
  ].filter(Boolean).join(' ');

  return (
    <div
      className={`uni-report-scroll freeze-cols-${frozenColCount}`}
      data-frozen-rows={freeze.rows || 1}
    >
      <table
        ref={tableRef}
        className={tableClass}
      >
        <thead>
          <tr>
            {columns.map((c, i) => (
              <SortableTh
                key={c.key}
                label={c.label}
                sortKey={c.key}
                activeKey={sort.key}
                direction={sort.dir}
                onSort={toggleSort}
                align={c.align || 'left'}
                className={i < frozenColCount ? 'uni-report-frozen-col' : ''}
                style={i < frozenColCount ? { left: colOffsets[i] ?? 0, zIndex: 3 } : {}}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedRows.map((r, ri) => (
            <tr key={r[rowKey] || ri}>
              {columns.map((c, i) => (
                <td
                  key={c.key}
                  data-label={c.label}
                  className={[
                    i < frozenColCount ? 'uni-report-frozen-col' : '',
                    isQuestionColumn(c.key) ? 'uni-report-question-col' : '',
                  ].filter(Boolean).join(' ')}
                  style={{
                    textAlign: c.align || 'left',
                    ...(i < frozenColCount
                      ? { left: colOffsets[i] ?? 0, zIndex: 1 }
                      : {}),
                  }}
                >
                  {c.render ? c.render(r) : (r.cells?.[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
